import React, { useRef } from "react";
import { Box, Typography, Stack, IconButton } from "@mui/material";
import CloverLiveEventCard from "./CloverEventCard"; // Update path if needed
import { useNavigate } from "react-router-dom";
import { CardWithTalent, useGetOnAirCard } from "../../services/api/card.api";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

// —— Netflix-ish tokens (design only)
export const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
  redHover: "#b20710",
};

const LiveEventsList: React.FC = () => {
  // Placeholder event handlers (can integrate zustand or modal context)
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollBy = (offset: number) => {
    scrollRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  };

  const handleJoin = (event: CardWithTalent) => {
    if (event.suit === "Clover") {
      navigate(`/user-live-stream/${event.id}?talent=${event.talent.id}`);
      return;
    }
  };

  const { data } = useGetOnAirCard({
    key: ["on-air-events"],
    params: { offset: 0, limit: 1000 },
  });

  return (
    <Box
      px={{ xs: 2, sm: 3, md: 4 }}
      mb={{ xs: 4, md: 6 }}
      sx={{
        color: COLORS.text,
        position: "relative",
        background:
          "radial-gradient(900px 400px at 8% 0%, rgba(229,9,20,.09), transparent 60%)",
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "20px", sm: "24px", md: "26px" },
            fontWeight: 900,
            fontFamily: "Lexend",
            color: COLORS.text,
            letterSpacing: "-0.01em",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            mb: { xs: 1, sm: 0 },
            "&::after": {
              content: '""',
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: COLORS.red,
              boxShadow: "0 0 12px rgba(229,9,20,0.6)",
            },
          }}
        >
          LIVE ZONE
        </Typography>

        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            width: { xs: "100%", md: "auto" },
            alignItems: "flex-end",
            justifyContent: { xs: "flex-end", md: "flex-end" },
          }}
        >
          {/* Scroll Left */}
          <IconButton
            onClick={() => scrollBy(-400)}
            sx={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.06)",
              border: `1px solid ${COLORS.border}`,
              backdropFilter: "blur(6px)",
              transition: "all .2s ease",
              color: COLORS.text,
              "&:hover": {
                backgroundColor: COLORS.red,
                borderColor: COLORS.red,
                transform: "scale(1.05)",
                boxShadow: "0 6px 18px rgba(229,9,20,0.35)",
              },
            }}
          >
            <ChevronLeftIcon size={18} />
          </IconButton>

          {/* Scroll Right */}
          <IconButton
            onClick={() => scrollBy(400)}
            sx={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.06)",
              border: `1px solid ${COLORS.border}`,
              backdropFilter: "blur(6px)",
              transition: "all .2s ease",
              color: COLORS.text,
              "&:hover": {
                backgroundColor: COLORS.red,
                borderColor: COLORS.red,
                transform: "scale(1.05)",
                boxShadow: "0 6px 18px rgba(229,9,20,0.35)",
              },
            }}
          >
            <ChevronRightIcon size={18} />
          </IconButton>
        </Box>
      </Box>

      <Typography
        sx={{
          mb: 4,
          font: { xs: "var(--body-sm)", md: "var(--body-md)" },
          color: COLORS.muted,
        }}
      >
        Discover what’s on-air now
      </Typography>

      {!data?.data || data.data.length === 0 ? (
        <Box
          textAlign="center"
          py={8}
          px={4}
          sx={{
            backgroundColor: COLORS.panel,
            borderRadius: 2,
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
          }}
        >
          <Typography variant="h3" sx={{ mb: 2 }}>
            🎭
          </Typography>
          <Typography
            variant="h5"
            fontWeight={800}
            gutterBottom
            sx={{ color: COLORS.text }}
          >
            No Live Events Currently
          </Typography>
          <Typography
            variant="body1"
            sx={{ maxWidth: 520, mx: "auto", color: COLORS.muted }}
          >
            We’re preparing something amazing for you. Stay tuned and be the
            first to join the next live experience.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", position: "relative", overflow: "hidden" }}>
          {/* Edge fades */}
          <Box
            sx={{
              pointerEvents: "none",
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: { xs: 16, sm: 24, md: 32 },
              background:
                "linear-gradient(90deg, rgba(11,11,11,1), rgba(11,11,11,0))",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              pointerEvents: "none",
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: { xs: 16, sm: 24, md: 32 },
              background:
                "linear-gradient(270deg, rgba(11,11,11,1), rgba(11,11,11,0))",
              zIndex: 1,
            }}
          />

          {/* Scroller */}
          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: { xs: 2, sm: 3 },
              overflowX: "auto",
              overflowY: "hidden",
              px: 1,
              pb: 1,
              scrollBehavior: "smooth",
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": { display: "none" }, // Chrome, Safari
            }}
          >
            {data.data.map((event) => (
              <CloverLiveEventCard
                key={event.id}
                startTime={event.start_date_time}
                endTime={event.end_time}
                talentId={event.talent.id}
                username={event.talent.username}
                profileImage={event.talent.profile_image}
                title={event.title}
                thumbnail={event?.primary_thumbnail as string}
                shortDescription={event.short_description}
                isLive={true}
                hasAccess={false}
                ticketPrice={event.price}
                onJoin={() => handleJoin(event)}
                onPurchase={() => {}}
                youtubeUrl={""}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LiveEventsList;
